import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import * as Constants from "../api/constants";
import "../adminConsoleTheme.css";

const ROLE_HEADER = "X-Admin-Role";
const SUPER_ADMIN = "SUPER_ADMIN";

/** Super admin: whatsapp/email follow-up wording, SPA base URLs, downloadable QR PNGs, booking attribution stats. */
export default function AdminMarketing() {
  const navigate = useNavigate();
  const { context } = useContext(Context);
  const role = String(context?.adminRole || "").toUpperCase();

  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [saving, setSaving] = useState(false);

  const [publicSiteBaseUrl, setPublicSiteBaseUrl] = useState("");
  const [whatsappBusinessDigits, setWhatsappBusinessDigits] = useState("");
  const [whatsappTemplate, setWhatsappTemplate] = useState("");

  /** Active theatres for poster dropdown + server-built scan URLs */
  const [posterTheatres, setPosterTheatres] = useState([]);
  /** "BOOK_NOW" or theatre id as string */
  const [posterSelection, setPosterSelection] = useState("BOOK_NOW");
  const [posterScanUrl, setPosterScanUrl] = useState("");
  const [posterPreviewObjectUrl, setPosterPreviewObjectUrl] = useState(null);
  const [posterPreviewLoading, setPosterPreviewLoading] = useState(false);
  const posterObjectUrlRef = useRef(null);

  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (role !== SUPER_ADMIN) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, role, navigate]);

  useEffect(() => {
    return () => {
      if (posterObjectUrlRef.current) {
        URL.revokeObjectURL(posterObjectUrlRef.current);
        posterObjectUrlRef.current = null;
      }
    };
  }, []);

  const headers = useMemo(
    () => ({
      [ROLE_HEADER]: role,
      "X-Admin-User": context?.adminUserName || "",
    }),
    [role, context?.adminUserName]
  );

  const showBanner = useCallback((variant, message) => {
    setBanner({ variant, message });
    window.setTimeout(() => setBanner(null), 5000);
  }, []);

  const trimPublicBase = useCallback((b) => {
    if (!b || typeof b !== "string") return "";
    return b.replace(/\/+$/, "");
  }, []);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(Constants.ADMIN_BOOKING_FOLLOWUP_SETTINGS, { headers }),
      axios.get(Constants.ADMIN_BOOKING_SOURCES_STATS, { headers }).catch(() => ({ data: [] })),
      axios.get(Constants.ADMIN_MARKETING_THEATRES_POSTER, { headers }).catch(() => ({ data: [] })),
      axios.get(Constants.SITE_MAINTENANCE_MODE).catch(() => ({ data: {} })),
    ])
      .then(([settingsRes, statsRes, theatresRes, maintenanceRes]) => {
        const dto = settingsRes?.data || {};
        setPublicSiteBaseUrl(dto.publicSiteBaseUrl ?? "");
        setWhatsappBusinessDigits(dto.whatsappBusinessDigits ?? "");
        setWhatsappTemplate(dto.whatsappBookingMessageTemplate ?? "");
        const s = statsRes?.data;
        setStats(Array.isArray(s) ? s : []);
        const tt = theatresRes?.data;
        setPosterTheatres(Array.isArray(tt) ? tt : []);
        const md = maintenanceRes?.data ?? {};
        setMaintenanceEnabled(md.enabled === true);
        setMaintenanceMessage(String(md.message || ""));
      })
      .catch((err) => {
        showBanner(
          "danger",
          err?.response?.data?.message || err?.message || "Could not load marketing settings."
        );
      })
      .finally(() => setLoading(false));
  }, [headers, showBanner]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (loading || role !== SUPER_ADMIN) return undefined;
    let cancelled = false;

    const refresh = async () => {
      setPosterPreviewLoading(true);
      try {
        if (posterSelection === "BOOK_NOW") {
          const base = trimPublicBase(publicSiteBaseUrl) || "https://www.partyboss.in";
          setPosterScanUrl(`${base}/#/booknow`);
        } else {
          const row = posterTheatres.find((r) => String(r.id) === String(posterSelection));
          setPosterScanUrl(row?.scanTargetUrl ?? "");
        }

        const variant = posterSelection === "BOOK_NOW" ? "BOOK_NOW" : "THEATRE";
        const theatreId = posterSelection === "BOOK_NOW" ? undefined : posterSelection;
        const { data } = await axios.get(
          Constants.adminMarketingColorEntryQrUrl(variant, theatreId),
          { headers, responseType: "blob" }
        );
        if (cancelled) return;
        const nextUrl = URL.createObjectURL(data);
        if (posterObjectUrlRef.current) {
          URL.revokeObjectURL(posterObjectUrlRef.current);
        }
        posterObjectUrlRef.current = nextUrl;
        setPosterPreviewObjectUrl(nextUrl);
      } catch {
        if (!cancelled) {
          if (posterObjectUrlRef.current) {
            URL.revokeObjectURL(posterObjectUrlRef.current);
            posterObjectUrlRef.current = null;
          }
          setPosterPreviewObjectUrl(null);
          showBanner("warning", "Could not build colour QR — ensure `partyboss.qr.logo.url` is a PNG/JPEG if you want the logo.");
        }
      } finally {
        if (!cancelled) setPosterPreviewLoading(false);
      }
    };

    refresh();
    return () => {
      cancelled = true;
    };
  }, [
    loading,
    role,
    posterSelection,
    posterTheatres,
    publicSiteBaseUrl,
    headers,
    showBanner,
    trimPublicBase,
  ]);

  const downloadPosterQr = useCallback(async () => {
    try {
      const variant = posterSelection === "BOOK_NOW" ? "BOOK_NOW" : "THEATRE";
      const theatreId = posterSelection === "BOOK_NOW" ? undefined : posterSelection;
      const { data } = await axios.get(Constants.adminMarketingColorEntryQrUrl(variant, theatreId), {
        headers,
        responseType: "blob",
      });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download =
        variant === "BOOK_NOW"
          ? "partyboss-entry-booknow-color.png"
          : `partyboss-entry-theatre-${theatreId}-color.png`;
      a.click();
      URL.revokeObjectURL(href);
    } catch {
      showBanner("danger", "Download failed — check network and admin role.");
    }
  }, [posterSelection, headers, showBanner]);

  const saveSettings = () => {
    setSaving(true);
    axios
      .put(
        Constants.ADMIN_BOOKING_FOLLOWUP_SETTINGS,
        {
          publicSiteBaseUrl: publicSiteBaseUrl.trim(),
          whatsappBusinessDigits: whatsappBusinessDigits.trim(),
          whatsappBookingMessageTemplate: whatsappTemplate,
        },
        { headers }
      )
      .then(() => {
        showBanner("success", "Saved post-book messaging settings.");
        loadAll();
      })
      .catch((err) => {
        showBanner(
          "danger",
          err?.response?.data?.message || err?.message || "Save failed."
        );
      })
      .finally(() => setSaving(false));
  };

  const saveMaintenance = () => {
    setMaintenanceSaving(true);
    axios
      .put(
        Constants.ADMIN_MAINTENANCE_MODE,
        { enabled: maintenanceEnabled, message: maintenanceMessage.trim() },
        { headers }
      )
      .then(() => {
        showBanner(
          "success",
          maintenanceEnabled
            ? "Maintenance gate is ON for public visitors."
            : "Maintenance gate is OFF — site flows normally."
        );
        loadAll();
      })
      .catch((err) => {
        showBanner(
          "danger",
          err?.response?.data?.message || err?.message || "Could not save maintenance mode."
        );
      })
      .finally(() => setMaintenanceSaving(false));
  };

  return (
    <AdminLayout title="Marketing &amp; attribution">
      {banner ? <Alert variant={banner.variant}>{banner.message}</Alert> : null}

      {loading ? (
        <div className="admin-console-spinner-wrap">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <section className="admin-console-section">
            <h2 className="admin-console-heading">Public site maintenance (launch gate)</h2>
            <p className="admin-console-intro">
              When <strong>ON</strong>, visitors to the SPA (non-admin URLs) see a full-page message instead of
              the normal routing; the welcome <strong>customer popup</strong> still appears above it so you can keep
              collecting leads during a pre-launch.
            </p>
            <div className="d-flex flex-column flex-lg-row gap-3 align-items-start mb-3">
              <Form.Check
                type="switch"
                id="pb-maintenance-enabled"
                className="text-nowrap mt-2"
                label={maintenanceEnabled ? "Maintenance gate ON" : "Maintenance gate OFF"}
                checked={maintenanceEnabled}
                disabled={maintenanceSaving}
                onChange={(e) => setMaintenanceEnabled(e.target.checked)}
              />
              <Button
                type="button"
                variant="warning"
                disabled={maintenanceSaving}
                className="text-dark"
                onClick={saveMaintenance}
              >
                {maintenanceSaving ? "Saving…" : "Save maintenance settings"}
              </Button>
            </div>
            <Form.Group className="mb-0">
              <Form.Label>Visitor-facing message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={maintenanceMessage}
                disabled={maintenanceSaving}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                maxLength={600}
              />
              <Form.Text className="text-muted">Stored up to ~500 chars server-side.</Form.Text>
            </Form.Group>
          </section>

          <section className="admin-console-section">
            <h2 className="admin-console-heading">Booking follow-up &amp; WhatsApp</h2>
            <p className="admin-console-intro">
              These values drive <strong>booking confirmation emails</strong> after Razorpay: My Booking QR,
              WhatsApp prefilled deeplink (<code>https://wa.me/&lt;digits&gt;?text=…</code>), and link targets for
              printed entry posters. Centre logo on <strong>colour</strong> posters: set Spring property{' '}
              <code>partyboss.qr.logo.url</code> to a <strong>PNG or JPEG</strong> (SVG cannot be painted in the
              QR middle).
            </p>
            <Form className="admin-console-stack">
              <Form.Group className="mb-3">
                <Form.Label>Public SPA base URL</Form.Label>
                <Form.Control
                  value={publicSiteBaseUrl}
                  onChange={(e) => setPublicSiteBaseUrl(e.target.value)}
                  placeholder="https://partyboss.in/quickpartyboss"
                  autoComplete="off"
                />
                <Form.Text className="text-muted">
                  No trailing slash. Example target: YOUR_BASE#/my-booking?phone=XXXXXXXXXX (hash-router).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>WhatsApp business number</Form.Label>
                <Form.Control
                  value={whatsappBusinessDigits}
                  onChange={(e) => setWhatsappBusinessDigits(e.target.value)}
                  placeholder="917287803015"
                  autoComplete="off"
                />
                <Form.Text className="text-muted">Digits only — country code + national number.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>WhatsApp prefilled message template</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={whatsappTemplate}
                  onChange={(e) => setWhatsappTemplate(e.target.value)}
                  placeholder={`Placeholders: \${customerName}, \${bookingReferenceId}, \${eventDate}, \${selecteTimeSlot}, \${theatreName}, \${totalAmount}, \${amountTobePaid}, \${myBookingUrl}`}
                />
              </Form.Group>

              <Button type="button" variant="primary" disabled={saving} onClick={saveSettings}>
                {saving ? "Saving…" : "Save settings"}
              </Button>
            </Form>
          </section>

          <section className="admin-console-section">
            <h2 className="admin-console-heading">Entry posters — full-colour QR (print)</h2>
            <p className="admin-console-intro">
              Choose <strong>Book now (all theatres)</strong> for <code>#/booknow</code> on your public SPA base —
              fourth standard option for reception / lobby. Pick a theatre name for venue-specific deeplinks
              (<code>#/quick-book</code> with id + readable name). High-resolution PNG uses a pink→violet gradient,
              blush background, and centre logo when <code>partyboss.qr.logo.url</code> is a raster image — ready for
              colour print and mounting at entries.
            </p>
            <div className="row g-3 align-items-start mb-3">
              <Form.Group className="col-md-5">
                <Form.Label>Poster target</Form.Label>
                <Form.Select
                  value={posterSelection}
                  onChange={(e) => setPosterSelection(e.target.value)}
                  aria-label="Choose theatre or generic book now"
                >
                  <option value="BOOK_NOW">Book now — all theatres (#/booknow)</option>
                  {posterTheatres.map((t) => (
                    <option key={String(t.id)} value={String(t.id)}>
                      {t.name || `Theatre ${t.id}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="col-md-7">
                <Form.Label className="d-block">Actions</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  <Button type="button" variant="outline-primary" disabled={posterPreviewLoading} onClick={downloadPosterQr}>
                    Download PNG
                  </Button>
                  {posterPreviewLoading ? (
                    <span className="text-muted align-self-center small">Rendering…</span>
                  ) : null}
                </div>
              </div>
            </div>
            {posterScanUrl ? (
              <p className="small text-break mb-2">
                <strong>Encodes:</strong> <code>{posterScanUrl}</code>
              </p>
            ) : null}
            <div className="rounded border bg-light p-3 d-inline-block">
              {posterPreviewObjectUrl ? (
                <img
                  src={posterPreviewObjectUrl}
                  alt="Colour entry QR preview"
                  style={{ maxWidth: "min(420px, 100%)", height: "auto" }}
                />
              ) : posterPreviewLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <span className="text-muted small">Preview unavailable</span>
              )}
            </div>
            <p className="text-muted small mt-3 mb-0">
              Legacy monochrome API links (no admin auth):{' '}
              <code style={{ wordBreak: 'break-all' }}>{Constants.QR_MY_BOOKING_PNG}</code>,{' '}
              <code style={{ wordBreak: 'break-all' }}>{Constants.qrTheatreQuickBookPngUrl('{id}', 'qr_theatre')}</code>
            </p>
          </section>

          <section className="admin-console-section">
            <h2 className="admin-console-heading">Booking source tags</h2>
            <p className="admin-console-intro">
              Counts of non-cancelled bookings by <code>booking_traffic_source</code> captured from QR / campaign
              query params (<code>?src=instagram</code>, <code>utm_source</code>). <code>__unset__</code> means
              legacy bookings with no tag.
            </p>
            {stats.length === 0 ? (
              <p className="text-muted">No aggregated rows yet.</p>
            ) : (
              <Table striped bordered hover size="sm" responsive className="admin-console-table mt-3">
                <thead>
                  <tr>
                    <th>Source tag</th>
                    <th>Bookings</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((row) => (
                    <tr key={String(row.sourceTag)}>
                      <td>
                        <code>{row.sourceTag}</code>
                      </td>
                      <td>{row.bookingCount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </section>

          <section className="admin-console-section pb-5">
            <h2 className="admin-console-heading">Roadmap (not in this console yet)</h2>
            <ul className="text-muted">
              <li>
                Fully automated WhatsApp pushes require Meta WhatsApp Cloud API / BSP — today we embed{' '}
                <code>wa.me</code> deeplinks only (customer taps to send).
              </li>
              <li>AI video generation / voiceover and Instagram &amp; YouTube publishing need OAuth APIs, quotas, and a compliance review.</li>
            </ul>
          </section>
        </>
      )}
    </AdminLayout>
  );
}
